// NOTICE: KAPTION-INTEGRATION
// Audience building helpers for WhatsApp campaigns.
// v2 - recursive toPlainId unwrapping for Vue reactivity bug
// Extracted for testability — the TagMultiSelectComboBox can produce nested
// {id: {id, type}, type} objects due to Vue reactivity cycles. These helpers
// normalize audience data at every stage.

/**
 * Normalize a value to a plain numeric/string ID.
 * Handles: number, string, {id: N, type: T} object, nested {id: {id: N}}.
 * @param {*} val
 * @returns {number|string|null}
 */
export function toPlainId(val) {
  if (val == null) return null;
  if (typeof val === 'object' && 'id' in val) {
    // eslint-disable-next-line no-console
    console.log(
      '[KAPTION:audience:toPlainId] unwrapping object:',
      JSON.stringify(val)
    );
    return toPlainId(val.id);
  }
  return val;
}

/**
 * Build a clean audience array from selected label and segment IDs.
 * @param {Array} labelIds - Raw IDs from label combobox (may contain objects)
 * @param {Array} segmentIds - Raw IDs from segment combobox (may contain objects)
 * @returns {Array<{id: number, type: string}>}
 */
export function buildAudience(labelIds, segmentIds) {
  // eslint-disable-next-line no-console
  console.log(
    '[KAPTION:audience:buildAudience] input labelIds:',
    JSON.stringify(labelIds),
    'segmentIds:',
    JSON.stringify(segmentIds)
  );
  const labels = (labelIds || [])
    .map(id => ({ id: toPlainId(id), type: 'Label' }))
    .filter(entry => entry.id != null);

  const segments = (segmentIds || [])
    .map(id => ({ id: toPlainId(id), type: 'Segment' }))
    .filter(entry => entry.id != null);

  const result = [...labels, ...segments];
  // eslint-disable-next-line no-console
  console.log(
    '[KAPTION:audience:buildAudience] output:',
    JSON.stringify(result)
  );
  return result;
}

/**
 * Sanitize an existing audience array — unwrap nested IDs and fix types.
 * Use before submitting to API.
 * @param {Array} audience - Raw audience from formState
 * @returns {Array<{id: number, type: string}>}
 */
export function sanitizeAudience(audience) {
  // eslint-disable-next-line no-console
  console.log(
    '[KAPTION:audience:sanitizeAudience] input:',
    JSON.stringify(audience)
  );
  if (!Array.isArray(audience)) return [];

  const result = audience
    .map(entry => {
      // If id is an object like {id: 26, type: 'Label'}, unwrap it
      // and prefer the inner type if the outer type is wrong
      if (entry.id != null && typeof entry.id === 'object') {
        return {
          id: toPlainId(entry.id),
          type: entry.id.type || entry.type,
        };
      }
      return { id: entry.id, type: entry.type };
    })
    .filter(entry => entry.id != null && entry.type);
  // eslint-disable-next-line no-console
  console.log(
    '[KAPTION:audience:sanitizeAudience] output:',
    JSON.stringify(result)
  );
  return result;
}

/**
 * Extract plain IDs for a given type from an audience array.
 * @param {Array} audience
 * @param {string} type - 'Label' or 'Segment'
 * @returns {Array<number>}
 */
export function getIdsForType(audience, type) {
  const result = (audience || [])
    .filter(a => a.type === type)
    .map(a => toPlainId(a.id))
    .filter(id => id != null);
  // eslint-disable-next-line no-console
  console.log(
    `[KAPTION:audience:getIdsForType] type=${type} input:`,
    JSON.stringify(audience),
    'output:',
    JSON.stringify(result)
  );
  return result;
}
